import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';

import { Button } from '@/components/Buttons';
import { getProductsQueryOptions } from '@/queries/products';
import { trackMP } from '@/shared/mp';

import ProductCard, { type CardFace, faceComponentName } from './ProductCard';
import ProductEligibilityResult from './ProductEligibilityResult';
import ProductPrice from './ProductPrice';
import useEligibilityBlocked from './useEligibilityBlocked';
import useProductCheckout from './useProductCheckout';

interface ExplorerDayPassProps {
  variant?: 'primary' | 'secondary';
  forEducator?: boolean;
  entryPoint?: string; // for analytics only
}

const LOOKUP_KEY = 'all-habitats-day-pass';

const WHATS_INCLUDED_IMAGE = 'https://zoolife.tv/assets/products/whats-included-l1b.webp';

const ExplorerDayPass = ({ variant = 'primary', forEducator, entryPoint }: ExplorerDayPassProps) => {
  const isPrimaryVariant = variant === 'primary';

  const { data: product } = useQuery({
    ...getProductsQueryOptions({
      lookupKey: LOOKUP_KEY,
    }),
    select: (data) => data.list[0],
  });
  const eligibilityBlocked = useEligibilityBlocked(product);
  const { start: startCheckout, isPending } = useProductCheckout();

  const trackedProps = { entryPoint, productLookupKey: LOOKUP_KEY };

  const renderCheckoutButton = (face: CardFace) => (
    <Button
      data-component={faceComponentName(face, 'checkout-button')}
      disabled={!product || isPending || eligibilityBlocked}
      loading={isPending}
      size="lg"
      className="w-full"
      onClick={() => {
        if (!product) return;
        startCheckout({
          productId: product._id,
          priceId: product.price.priceId,
          productLookupKey: product.productLookupKey,
          entryPoint,
        });
      }}
    >
      Select Pass
    </Button>
  );

  return (
    <ProductCard.Root data-component="all-habitats-day-pass" className={clsx(isPrimaryVariant && 'bg-beige-light')}>
      <ProductCard.Front className="justify-center gap-3">
        <div
          className={clsx(
            'relative w-full rounded-t-2xl bg-[url("https://zoolife.tv/assets/products/l1.webp")] bg-cover bg-center',
            isPrimaryVariant ? 'aspect-[336/143]' : 'aspect-[336/97]',
          )}
        />
        <h4 className="preflight preflight-h4 mx-4 max-w-[274px] text-center text-2xl/7 font-bold text-green-900">
          {forEducator ? 'All-Access Pass & Virtual Field Trip' : '24h Unlimited Pass & Guided Experience'}
        </h4>
        <ProductPrice currency={product?.price.currency} price={product?.price.price} className="px-4" />
        <p className="preflight preflight-p mx-4 max-w-[274px] text-center text-base/[18px] text-gray-500">
          {forEducator
            ? 'An unforgettable 1-2 hour guided tour with interactive tool-tips. Visit all the habitats, at your students’ pace!'
            : 'A self-paced, guided expedition showcasing the habitats from most-loved to best kept secrets - LIVE and recapped - with fun facts at each stop.'}
        </p>
        <div className={clsx('flex w-full flex-col items-center gap-3 px-4', isPrimaryVariant ? 'pb-6' : 'pb-4')}>
          {renderCheckoutButton('front')}
          {product && <ProductEligibilityResult result={product.eligibility} />}
          <ProductCard.WhatsIncludedTrigger onOpen={() => trackMP('click-whats-included', trackedProps)} />
        </div>
      </ProductCard.Front>

      <ProductCard.WhatsIncluded>
        <ProductCard.BackButton />
        <ProductCard.WhatsIncludedHeading>
          A guided experience of zoolife&apos;s best and brightest habitats
        </ProductCard.WhatsIncludedHeading>
        <ProductCard.WhatsIncludedImage src={WHATS_INCLUDED_IMAGE} alt="A family watching a habitat stream together" />
        <ProductCard.Benefits product={product} />
        <ProductCard.CheckoutGroup>
          {renderCheckoutButton('whats-included')}
          {product && <ProductEligibilityResult result={product.eligibility} />}
        </ProductCard.CheckoutGroup>
      </ProductCard.WhatsIncluded>
    </ProductCard.Root>
  );
};

export default ExplorerDayPass;
